import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Search } from "lucide-react";

/** Ctrl+K jump box — find any PO, GRN, sales order, box, batch, school, party or title. */
export const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    const key = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, []);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setHits([]); return; }
    const t = setTimeout(() => {
      api.get("/search", { params: { q: term } }).then((r) => setHits(r.data)).catch(() => {});
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  const go = (h) => {
    setOpen(false); setQ(""); setHits([]);
    nav(`${h.to}?q=${encodeURIComponent(h.label)}`);
  };

  const groups = hits.reduce((acc, h) => ({ ...acc, [h.kind]: [...(acc[h.kind] || []), h] }), {});

  return (
    <>
      <button data-testid="global-search-btn" onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 border border-border text-xs text-muted-foreground hover:bg-accent transition-colors duration-150">
        <Search size={14} /> Search
        <span className="mono hidden sm:inline text-[10px] border border-border px-1">Ctrl K</span>
      </button>
      <CommandDialog open={open} onOpenChange={setOpen} title="Search" description="Find any document or master record">
        <Command shouldFilter={false} data-testid="global-search-dialog">
          <CommandInput data-testid="global-search-input" value={q} onValueChange={setQ}
            placeholder="PO / GRN / SO / box no, school, party or title…" />
          <CommandList>
            {q.trim().length >= 2 && !hits.length && <CommandEmpty>Nothing matches “{q.trim()}”.</CommandEmpty>}
            {Object.entries(groups).map(([kind, items]) => (
              <CommandGroup key={kind} heading={kind}>
                {items.map((h, i) => (
                  <CommandItem key={`${kind}-${i}`} value={`${kind}-${i}-${h.label}`} onSelect={() => go(h)}
                    data-testid={`global-hit-${h.label}`}>
                    <span className="font-medium">{h.label}</span>
                    {h.sub && <span className="ml-2 text-xs text-muted-foreground">{h.sub}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  );
};

/** Reads ?q= put there by the jump box so a page opens already filtered. */
export const urlQuery = () => {
  try { return new URLSearchParams(window.location.search).get("q") || ""; }
  catch (e) { return ""; }
};
